<template>
  <v-dialog
    v-model="openFormDialog"
    max-width="850px"
    persistent
    content-class="form-dialog"
  >
    <div class="scroll-top"></div>
    <div class="register-form-container">
      <form class="register-form">
        <v-container>
          <v-row justify="space-between">
            <h1 class="register-title">
              <span v-if="selectedLeader">{{ $t('reg.editTitle') }}</span
              ><span v-else>{{ $t('reg.title') }}</span>
            </h1>
            <v-btn color="secondary" icon @click="closeDialog">
              <v-icon>mdi-window-close</v-icon>
            </v-btn>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="leader.name"
                :error-messages="nameErrors"
                :label="nameText + ' *'"
                required
                autofocus
                maxlength="50"
                @input="$v.leader.name.$touch()"
                @blur="$v.leader.name.$touch()"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="leader.lastname"
                :error-messages="lastnameErrors"
                :label="lastnameText + ' *'"
                required
                maxlength="50"
                @input="$v.leader.lastname.$touch()"
                @blur="$v.leader.lastname.$touch()"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="leader.idNumber"
                :error-messages="idNumberErrors"
                :label="idText + ' *'"
                maxlength="30"
                required
                @input="$v.leader.idNumber.$touch()"
                @blur="$v.leader.idNumber.$touch()"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="leader.phone"
                :error-messages="phoneErrors"
                :label="mobileText + ' *'"
                maxlength="15"
                required
                @input="$v.leader.phone.$touch()"
                @blur="$v.leader.phone.$touch()"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="leader.country"
                :error-messages="countryErrors"
                :items="countries"
                :label="countryText + ' *'"
                required
                @change="$v.leader.country.$touch()"
                @blur="$v.leader.country.$touch()"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="leader.city"
                :error-messages="cityErrors"
                :label="cityText + ' *'"
                maxlength="50"
                required
                @input="$v.leader.city.$touch()"
                @blur="$v.leader.city.$touch()"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="leader.firstLanguage"
                :items="languageList"
                :label="nativeLangText + ' *'"
                required
                :error-messages="firstLanguageErrors"
                @change="$v.leader.firstLanguage.$touch()"
                @blur="$v.leader.firstLanguage.$touch()"
              >
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                v-model="leader.secondLanguage"
                :items="languageList"
                :search-input.sync="searchSecondLanguage"
                item-text="text"
                item-value="value"
                :label="secondLangText"
                class="uppercase-input"
                clearable
                :return-object="false"
              >
                <template v-slot:selection="{ item }">
                  {{ translateListValue(item) }}
                </template>
              </v-combobox>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="leader.email"
                :error-messages="emailErrors"
                :label="emailText + ' *'"
                required
                @input="$v.leader.email.$touch()"
                @blur="$v.leader.email.$touch()"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="leader.unities"
                :items="unitiesList"
                :label="unityText"
                multiple
              >
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="leader.profiles"
                :items="profilesList"
                :label="profileText"
                multiple
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="leader.range"
                :items="rangeList"
                :label="rangeText"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-combobox
                ref="professionsCombobox"
                v-model="leader.professions"
                :items="professionItems"
                :search-input.sync="searchProfessions"
                item-text="text"
                item-value="value"
                hide-selected
                :label="professionText"
                multiple
                small-chips
                deletable-chips
                :return-object="false"
                @change="
                  handleOtherOption(
                    'professions',
                    'addingOtherProfessions',
                    'searchProfessions',
                    'professionsCombobox'
                  )
                "
              >
                <template v-slot:item="{ item }">
                  <span :class="getOtherOptionClass(item)">
                    {{ item.text }}
                  </span>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-if="searchProfessions"
                          >{{ $t('reg.press') }}
                        </span>
                        <span v-else>{{ $t('reg.enterAndPressF') }} </span>
                        <kbd>{{ $t('reg.enter') }}</kbd> {{ $t('reg.toAdd') }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ attrs, item, parent }">
                  <v-chip
                    v-bind="attrs"
                    close
                    small
                    @click:close="parent.selectItem(item)"
                  >
                    <strong>{{ translateListValue(item) }}</strong
                    >&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                ref="postGradesCombobox"
                v-model="leader.postGrades"
                :items="postGradesItems"
                :search-input.sync="searchPostGrades"
                item-text="text"
                item-value="value"
                hide-selected
                :label="postgradeText"
                multiple
                :return-object="false"
                @change="
                  handleOtherOption(
                    'postGrades',
                    'addingOtherPostGrades',
                    'searchPostGrades',
                    'postGradesCombobox'
                  )
                "
              >
                <template v-slot:item="{ item }">
                  <span :class="getOtherOptionClass(item)">
                    {{ item.text }}
                  </span>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-if="searchPostGrades">{{
                          $t('reg.press')
                        }}</span>
                        <span v-else>{{ $t('reg.enterAndPress') }}</span>
                        <kbd>{{ $t('reg.enter') }}</kbd> {{ $t('reg.toAdd') }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ attrs, item, parent }">
                  <v-chip
                    v-bind="attrs"
                    close
                    small
                    @click:close="parent.selectItem(item)"
                  >
                    <strong>{{ translateListValue(item) }}</strong
                    >&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-combobox
                ref="specialtiesCombobox"
                v-model="leader.specialties"
                :items="specialtyItems"
                :search-input.sync="searchSpecialties"
                item-text="text"
                item-value="value"
                hide-selected
                :label="specialtyText"
                multiple
                small-chips
                deletable-chips
                :return-object="false"
                @change="
                  handleOtherOption(
                    'specialties',
                    'addingOtherSpecialties',
                    'searchSpecialties',
                    'specialtiesCombobox'
                  )
                "
              >
                <template v-slot:item="{ item }">
                  <span :class="getOtherOptionClass(item)">
                    {{ item.text }}
                  </span>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-if="searchSpecialties"
                          >{{ $t('reg.press') }}
                        </span>
                        <span v-else>{{ $t('reg.enterAndPressF') }} </span>
                        <kbd>{{ $t('reg.enter') }}</kbd> {{ $t('reg.toAdd') }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ attrs, item, parent }">
                  <v-chip
                    v-bind="attrs"
                    close
                    small
                    @click:close="parent.selectItem(item)"
                  >
                    <strong>{{ translateListValue(item) }}</strong
                    >&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                ref="subSpecialtiesCombobox"
                v-model="leader.subSpecialties"
                :items="subSpecialtyItems"
                :search-input.sync="searchSubSpecialties"
                item-text="text"
                item-value="value"
                hide-selected
                :label="subSpecialtyText"
                multiple
                small-chips
                deletable-chips
                :return-object="false"
                @change="
                  handleOtherOption(
                    'subSpecialties',
                    'addingOtherSubSpecialties',
                    'searchSubSpecialties',
                    'subSpecialtiesCombobox'
                  )
                "
              >
                <template v-slot:item="{ item }">
                  <span :class="getOtherOptionClass(item)">
                    {{ item.text }}
                  </span>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-if="searchSubSpecialties"
                          >{{ $t('reg.press') }}
                        </span>
                        <span v-else>{{ $t('reg.enterAndPressF') }} </span>
                        <kbd>{{ $t('reg.enter') }}</kbd> {{ $t('reg.toAdd') }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ attrs, item, parent }">
                  <v-chip
                    v-bind="attrs"
                    close
                    small
                    @click:close="parent.selectItem(item)"
                  >
                    <strong>{{ translateListValue(item) }}</strong
                    >&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-select
                v-model="leader.criteria"
                :items="criteriaList"
                :label="criteriaText"
                multiple
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-combobox
                v-model="leader.cientificAs"
                :search-input.sync="searchCientificAs"
                hide-selected
                :label="associationsText"
                multiple
                small-chips
                deletable-chips
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="register-item-title">
                        <span v-if="searchCientificAs">
                          {{ $t('reg.press') }}
                          <kbd>{{ $t('reg.enter') }}</kbd> {{ $t('reg.toAdd') }}
                        </span>
                        <span v-else>
                          {{ $t('reg.enterAssociation') }}
                          <kbd>{{ $t('reg.enter') }}</kbd> {{ $t('reg.toAdd') }}
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ attrs, item, parent }">
                  <v-chip
                    v-bind="attrs"
                    close
                    small
                    @click:close="parent.selectItem(item)"
                  >
                    <strong>{{ item }}</strong
                    >&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
          </v-row>
          <!-- CV UPLOAD -->
          <v-row>
            <v-col :cols="6" class="cv-upload-container">
              <v-file-input
                v-model="pdfFile"
                show-size
                :label="cvText"
                :placeholder="getPdfName()"
                accept=".pdf"
                truncate-length="70"
                append-icon="mdi-pdf-box"
                :hint="cvHintText"
                :persistent-hint="true"
              ></v-file-input>
              <div class="cv-delete-container">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      @click="removeCV()"
                      v-bind="attrs"
                      v-on="on"
                      class="cv-delete-button"
                      v-show="leader.cvUrl"
                    >
                      mdi-close-circle
                    </v-icon>
                  </template>
                  <span>{{ $t('reg.deleteCv') }}</span>
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="6">
              <v-menu
                v-model="endContractDatePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="leader.endContract"
                    :label="endContractText"
                    clearable
                    color="primary"
                    prepend-icon="mdi-calendar"
                    readonly
                    hide-details="true"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="leader.endContract"
                  no-title
                  scrollable
                  :locale="locale"
                  @input="endContractDatePicker = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea v-model="leader.observations" rows="2">
                <template v-slot:label>
                  <div>
                    {{ $t('fields.observations') }}
                    <small>{{ $t('reg.additionalInfo') }}</small>
                  </div>
                </template>
              </v-textarea>
            </v-col>
          </v-row>
          <!-- PUBLICATIONS -->
          <v-row class="publications-container">
            <v-col cols="12">
              <v-row>
                <h3 class="publications-title">
                  {{ $t('reg.publications') }}
                </h3>
                <v-spacer></v-spacer>
                <v-btn
                  class="ml-4 mt-1 secondary"
                  x-small
                  @click="addPublication"
                  v-if="!showPublicationForm"
                >
                  {{ $t('reg.addPublication') }}
                </v-btn>
              </v-row>
              <div v-if="showPublicationForm">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="publication.name"
                      ref="publicationName"
                      color="secondary"
                      :label="nameText"
                      maxlength="150"
                      dense
                      hide-details="true"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="12">
                    <v-combobox
                      v-model="publication.authors"
                      :search-input.sync="searchAuthors"
                      hide-selected
                      hide-details="true"
                      :label="authorsText"
                      dense
                      multiple
                      small-chips
                      deletable-chips
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title class="register-item-title">
                              <span v-if="searchAuthors">
                                {{ $t('reg.press') }}
                                <kbd>{{ $t('reg.enter') }}</kbd>
                                {{ $t('reg.toAdd') }}
                              </span>
                              <span v-else>
                                {{ $t('reg.enterAuthor') }}
                                <kbd>{{ $t('reg.enter') }}</kbd>
                                {{ $t('reg.toAdd') }}
                              </span>
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-combobox>
                  </v-col>
                  <v-col cols="12" md="10">
                    <div></div>
                    <v-text-field
                      v-model="publication.magazine"
                      color="secondary"
                      :label="magazineText"
                      maxlength="100"
                      dense
                      hide-details="true"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <div></div>
                    <v-text-field
                      v-model="publication.year"
                      color="secondary"
                      :label="yearText"
                      type="number"
                      dense
                      hide-details="true"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-btn class="mt-1" small @click="cancelPublication">
                      {{ $t('common.cancel') }}
                    </v-btn>
                    <v-btn
                      class="ml-4 mt-1 secondary"
                      small
                      @click="savePublication"
                    >
                      {{ $t('reg.savePublication') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
              <div v-else>
                <div v-if="leader.publications && leader.publications.length">
                  <div
                    v-for="(p, index) in leader.publications"
                    :key="`pub-${index}`"
                    class="publication-item-container"
                  >
                    <div class="publication-item">
                      <div>
                        <span class="bullet">&bull;</span>
                        <span class="publication-name">{{ p.name }}</span>
                      </div>
                      <div class="publication-info">
                        {{ getPublicationInfo(p) }}
                      </div>
                    </div>
                    <div class="publication-actions">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            small
                            @click="editPublication(p, index)"
                            v-bind="attrs"
                            v-on="on"
                            class="mr-2 item-action"
                          >
                            mdi-pencil
                          </v-icon>
                        </template>
                        <span> {{ $t('common.edit') }} </span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            small
                            @click="removePublication(p, index)"
                            v-bind="attrs"
                            v-on="on"
                            class="item-action"
                          >
                            mdi-close-circle
                          </v-icon>
                        </template>
                        <span> {{ $t('common.delete') }} </span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
                <div v-else class="no-publications mt-4">
                  {{ $t('reg.noPublications') }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider
            class="mt-4 mb-4"
            v-show="!showPublicationForm"
          ></v-divider>

          <v-row v-show="!showPublicationForm">
            <v-col cols="12">
              <v-btn
                class="mr-4 register-button primary"
                @click="submit"
                :disabled="registering"
              >
                <span v-if="registering"
                  ><v-progress-circular
                    indeterminate
                    color="primary"
                  ></v-progress-circular
                ></span>
                <span v-else-if="selectedLeader">
                  {{ $t('reg.saveChanges') }} </span
                ><span v-else>{{ $t('reg.register') }} </span>
              </v-btn>

              <v-btn
                class="mr-4 register-button"
                @click="closeDialog"
                :disabled="registering"
              >
                {{ $t('common.cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </form>
    </div>
  </v-dialog>
</template>

<script>
import { auth } from '../firebase'
import { validationMixin } from 'vuelidate'
import _cloneDeep from 'lodash/cloneDeep'
import { required, maxLength, email } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import {
  COUNTRIES,
  PROFESSIONS,
  POSTGRADES,
  SPECIALTIES,
  SUBSPECIALTIES,
  PROFILES,
  CRITERIA,
  LANGUAGES,
  UNITIES,
  CATEGORIES,
  RANGES
} from '@/consts/lists'
import {
  getPublicationInfo,
  translateList,
  translateListValue
} from '@/utils/common'
import { isUserAdmin } from '@/consts/common'

export default {
  mixins: [validationMixin],
  validations: {
    leader: {
      name: { required, maxLength: maxLength(50) },
      lastname: { required, maxLength: maxLength(50) },
      idNumber: { required, maxLength: maxLength(30) },
      country: { required },
      city: { required },
      firstLanguage: { required },
      phone: { required, maxLength: maxLength(15) },
      email: { required, email }
    }
  },

  data: () => ({
    isAdmin: isUserAdmin(auth.currentUser.email),
    leader: {},
    searchProfessions: null,
    searchPostGrades: null,
    searchSecondLanguage: null,
    searchSpecialties: null,
    searchSubSpecialties: null,
    searchCientificAs: null,
    searchAuthors: null,
    otherOptionValue: '__OTHER_OPTION__',
    addingOtherProfessions: false,
    addingOtherPostGrades: false,
    addingOtherSpecialties: false,
    addingOtherSubSpecialties: false,
    countries: COUNTRIES,
    professionsList: translateList(PROFESSIONS),
    postGradesList: translateList(POSTGRADES),
    specialtiesList: translateList(SPECIALTIES),
    subSpecialtiesList: translateList(SUBSPECIALTIES),
    profilesList: translateList(PROFILES),
    criteriaList: translateList(CRITERIA),
    languageList: translateList(LANGUAGES),
    unitiesList: UNITIES,
    categoryList: translateList(CATEGORIES, false),
    rangeList: translateList(RANGES, false),
    editablePublications: {},
    showPublicationForm: false,
    publication: {},
    selectedPublication: null,
    pdfFile: null,
    maxFileSize: 20971520, //20x1024x1024 bytes
    endContractDatePicker: false,
    locale: localStorage.getItem('bd-kol-lang') || 'es'
  }),
  watch: {
    pdfFile(newVal) {
      if (newVal) {
        if (newVal.type != 'application/pdf') {
          alert(this.errorPdfFormatText)
          setTimeout(() => {
            this.clearPdfFile()
          }, 0)
        } else if (newVal.size > this.maxFileSize) {
          alert(this.errorPdfSizeText)
          this.clearPdfFile()
        }
      }
    },
    openFormDialog(newVal) {
      if (newVal) {
        setTimeout(() => {
          document.getElementsByClassName('scroll-top')[0].scrollIntoView()
        }, 0)
      } else {
        //Dialog closes
        this.$v.leader.$reset()
        this.initForm()
      }
    },
    selectedLeader(newVal) {
      if (newVal) {
        this.leader = _cloneDeep(newVal)
      } else {
        this.leader = {}
      }
    },
    searchProfessions(val) {
      if (val) {
        this.addingOtherProfessions = false
        this.searchProfessions = val.toUpperCase()
      }
    },
    searchPostGrades(val) {
      if (val) {
        this.addingOtherPostGrades = false
        this.searchPostGrades = val.toUpperCase()
      }
    },
    searchSpecialties(val) {
      if (val) {
        this.addingOtherSpecialties = false
        this.searchSpecialties = val.toUpperCase()
      }
    },
    searchSubSpecialties(val) {
      if (val) {
        this.addingOtherSubSpecialties = false
        this.searchSubSpecialties = val.toUpperCase()
      }
    },
    searchCientificAs(val) {
      if (val) this.searchCientificAs = val.toUpperCase()
    }
  },

  computed: {
    nameErrors() {
      const errors = []
      if (!this.$v.leader.name.$dirty) return errors
      !this.$v.leader.name.maxLength && errors.push(this.max50charsText)
      !this.$v.leader.name.required && errors.push(this.requiredText)
      return errors
    },
    lastnameErrors() {
      const errors = []
      if (!this.$v.leader.lastname.$dirty) return errors
      !this.$v.leader.lastname.maxLength && errors.push(this.max50charsText)
      !this.$v.leader.lastname.required && errors.push(this.requiredText)
      return errors
    },
    emailErrors() {
      const errors = []
      if (!this.$v.leader.email.$dirty) return errors
      !this.$v.leader.email.email && errors.push(this.validEmailText)
      !this.$v.leader.email.required && errors.push(this.requiredText)
      return errors
    },
    idNumberErrors() {
      const errors = []
      if (!this.$v.leader.idNumber.$dirty) return errors
      !this.$v.leader.idNumber.maxLength && errors.push(this.max20charsText)
      !this.$v.leader.idNumber.required && errors.push(this.requiredText)
      return errors
    },
    countryErrors() {
      const errors = []
      if (!this.$v.leader.country.$dirty) return errors
      !this.$v.leader.country.required && errors.push(this.requiredText)
      return errors
    },
    cityErrors() {
      const errors = []
      if (!this.$v.leader.city.$dirty) return errors
      !this.$v.leader.city.required && errors.push(this.requiredText)
      return errors
    },
    phoneErrors() {
      const errors = []
      if (!this.$v.leader.phone.$dirty) return errors
      !this.$v.leader.phone.required && errors.push(this.requiredText)
      !this.$v.leader.phone.maxLength && errors.push(this.max15charsText)
      return errors
    },
    firstLanguageErrors() {
      const errors = []
      if (!this.$v.leader.firstLanguage.$dirty) return errors
      !this.$v.leader.firstLanguage.required && errors.push(this.requiredText)
      return errors
    },
    professionItems() {
      return this.addingOtherProfessions
        ? []
        : this.getItemsWithOther(this.professionsList, this.$t('reg.otherF'))
    },
    postGradesItems() {
      return this.addingOtherPostGrades
        ? []
        : this.getItemsWithOther(this.postGradesList, this.$t('reg.other'))
    },
    specialtyItems() {
      return this.addingOtherSpecialties
        ? []
        : this.getItemsWithOther(this.specialtiesList, this.$t('reg.otherF'))
    },
    subSpecialtyItems() {
      return this.addingOtherSubSpecialties
        ? []
        : this.getItemsWithOther(this.subSpecialtiesList, this.$t('reg.otherF'))
    },
    ...mapState({
      openFormDialog: state => state.leaderStore.openFormDialog,
      registering: state => state.leaderStore.registering,
      selectedLeader: state => state.leaderStore.selectedLeader
    }),
    nameText() {
      return this.$t('fields.name')
    },
    lastnameText() {
      return this.$t('fields.lastname')
    },
    countryText() {
      return this.$t('fields.country')
    },
    cityText() {
      return this.$t('fields.city')
    },
    professionText() {
      return this.$t('fields.profession')
    },
    postgradeText() {
      return this.$t('reg.postgrade')
    },
    specialtyText() {
      return this.$t('fields.specialty')
    },
    subSpecialtyText() {
      return this.$t('fields.subSpecialty')
    },
    profileText() {
      return this.$t('fields.profile')
    },
    criteriaText() {
      return this.$t('fields.criteria')
    },
    associationsText() {
      return this.$t('reg.associations')
    },
    nativeLangText() {
      return this.$t('reg.nativeLang')
    },
    secondLangText() {
      return this.$t('reg.secondLang')
    },
    unityText() {
      return this.$t('fields.unity')
    },
    categoryText() {
      return this.$t('fields.category')
    },
    rangeText() {
      return this.$t('fields.range')
    },
    mobileText() {
      return this.$t('fields.mobile')
    },
    idText() {
      return this.$t('reg.id')
    },
    emailText() {
      return this.$t('fields.email')
    },
    cvText() {
      return this.$t('reg.cv')
    },
    cvHintText() {
      return this.$t('reg.cvHint')
    },
    authorsText() {
      return this.$t('reg.authors')
    },
    magazineText() {
      return this.$t('reg.magazine')
    },
    yearText() {
      return this.$t('reg.year')
    },
    errorPdfFormatText() {
      return this.$t('reg.errorPdfFormat')
    },
    errorPdfSizeText() {
      return this.$t('reg.errorPdfSize')
    },
    max50charsText() {
      return this.$t('reg.max50chars')
    },
    max20charsText() {
      return this.$t('reg.max20chars')
    },
    max15charsText() {
      return this.$t('reg.max15chars')
    },
    requiredText() {
      return this.$t('reg.required')
    },
    validEmailText() {
      return this.$t('reg.validEmail')
    },
    invalidYearPubText() {
      return this.$t('reg.invalidYearPub')
    },
    nameRequiredPubText() {
      return this.$t('reg.nameRequiredPub')
    },
    formInvalidText() {
      return this.$t('reg.formInvalid')
    },
    endContractText() {
      return this.$t('fields.endContractDate')
    }
  },
  created() {
    this.initForm()
  },
  methods: {
    getPublicationInfo: getPublicationInfo,
    translateListValue: translateListValue,
    initForm() {
      this.leader = {
        name: '',
        lastname: '',
        idNumber: '',
        country: null,
        city: '',
        phone: '',
        email: '',
        professions: null,
        postGrades: null,
        specialties: null,
        subSpecialties: null,
        profiles: null,
        criteria: null,
        cientificAs: null,
        firstLanguage: '',
        secondLanguage: '',
        unities: ['MDS'],
        category: '',
        range: '',
        observations: '',
        publications: null,
        endContract: ''
      }
      this.resetPublicationsForm()
      this.pdfFile = null
      this.searchProfessions = null
      this.searchPostGrades = null
      this.searchSpecialties = null
      this.searchSubSpecialties = null
      this.searchCientificAs = null
      this.addingOtherProfessions = false
      this.addingOtherPostGrades = false
      this.addingOtherSpecialties = false
      this.addingOtherSubSpecialties = false
    },
    resetPublicationsForm() {
      this.publication = {
        name: '',
        authors: null,
        magazine: '',
        year: ''
      }
      this.searchPostGrades = null
      this.selectedPublication = null
    },
    scrollToPublication() {
      let self = this
      setTimeout(() => {
        let dialog = document.getElementsByClassName(
          'v-dialog v-dialog--active form-dialog'
        )[0]
        dialog.scrollTop = dialog.scrollHeight
        self.$refs.publicationName.focus()
      }, 0)
    },
    getItemsWithOther(list, text) {
      return [...list, { value: this.otherOptionValue, text }]
    },
    getOtherOptionClass(item) {
      return item.value === this.otherOptionValue ? 'other-option-item' : ''
    },
    handleOtherOption(field, flag, searchField, refName) {
      if (!Array.isArray(this.leader[field])) return
      if (!this.leader[field].includes(this.otherOptionValue)) {
        this[flag] = false
        return
      }

      this.leader[field] = this.leader[field].filter(
        value => value !== this.otherOptionValue
      )
      this[flag] = true
      this[searchField] = null

      this.$nextTick(() => {
        const combobox = this.$refs[refName]
        if (!combobox) return
        combobox.isMenuActive = true
        if (combobox.focus) combobox.focus()
      })
    },
    addPublication() {
      this.showPublicationForm = true
      this.scrollToPublication()
    },
    cancelPublication() {
      this.showPublicationForm = false
      this.resetPublicationsForm()
    },
    savePublication() {
      if (this.publication.year > new Date().getFullYear()) {
        alert(this.invalidYearPubText)
      } else if (!this.publication.name) {
        alert(this.nameRequiredPubText)
      } else {
        if (this.selectedPublication === null) {
          //Saving new publication
          if (!this.leader.publications) {
            this.leader.publications = []
          }
          this.leader.publications.push(_cloneDeep(this.publication))
        } else {
          //Editing publication
          this.leader.publications[this.selectedPublication] = _cloneDeep(
            this.publication
          )
        }
        this.resetPublicationsForm()
        this.showPublicationForm = false
      }
    },
    submit() {
      this.$v.leader.$touch()
      if (this.$v.leader.$invalid) {
        alert(this.formInvalidText)
      } else if (this.selectedLeader) {
        this.$store.dispatch('leaderStore/updateLeader', {
          leader: this.leader,
          pdfFile: this.pdfFile
        })
      } else {
        this.$store.dispatch('leaderStore/addLeader', {
          leader: this.leader,
          pdfFile: this.pdfFile
        })
      }
    },
    closeDialog() {
      this.$store.dispatch('leaderStore/openFormDialog', false)
    },
    editPublication(p, index) {
      this.publication = _cloneDeep(p)
      this.showPublicationForm = true
      this.selectedPublication = index
      this.scrollToPublication()
    },
    removePublication(index) {
      this.leader.publications.splice(index, 1)
    },
    clearPdfFile() {
      setTimeout(() => {
        this.pdfFile = null
      }, 0)
    },
    getPdfName() {
      if (this.leader.cv && this.leader.cvUrl) {
        return this.leader.cv.replace('cv/', '')
      }
      return null
    },
    removeCV() {
      this.leader.cvUrl = null
    }
  }
}
</script>
<style lang="scss">
/*
.form-dialog {
  scroll-behavior: smooth;
}*/
.register-form-container {
  display: flex;
  justify-content: center;
  background-color: #fff;

  .success-message {
    color: #2e478d;
  }

  .register-form {
    width: 100%;
    padding: 10px 10px 10px 10px;
    max-width: 800px;

    .register-title {
      text-align: left;
      color: #2e478d;
      font-size: 20px;
    }
    .register-subtitle {
      font-size: 15px;
    }
    .register-button {
      float: right;
      font-size: 15px;
    }
    @media only screen and (min-width: 600px) {
      .register-title {
        font-size: x-large;
      }
      .register-subtitle {
        font-size: large;
      }
    }
    .publications-container {
      padding: 0 14px;
      background: #eff7ff;
      border-radius: 5px;

      .publications-title {
        color: #16365d;
      }
      .no-publications {
        color: #5f5f5f;
        font-size: 15px;
        text-align: center;
      }
      .bullet {
        color: #31649e;
        font-size: 24px;
        line-height: 27px;
        float: left;
      }

      .publication-name {
        font-weight: bold;
        padding-left: 2px;
      }
      .publication-info {
        color: #5d5d5d;
        font-size: 15px;
        padding-left: 10px;
      }

      .publication-item-container {
        display: flex;
        padding: 5px;
        border: 1px solid #a3b4c7;
        border-radius: 5px;
        margin-top: 5px;

        .publication-item {
          width: 90%;
        }
        .publication-actions {
          width: 10%;
          text-align: right;

          .item-action {
            color: #5885b7;
            &:hover {
              color: #31649e;
              transform: scale3d(1.3, 1.3, 1.3);
            }
          }
        }
      }
    }

    .cv-upload-container {
      display: flex;

      .cv-delete-container {
        display: flex;
        flex-direction: row;
        align-items: center;

        .cv-delete-button {
          margin-left: 12px;
          color: #9e9e9e;

          &:hover {
            color: #9e9e9e;
            transform: scale3d(1.3, 1.3, 1.3);
          }
        }
      }
    }
  }
}
.register-item-title.v-list-item__title {
  white-space: normal;
}

.other-option-item {
  color: #ff6e00;
  font-weight: 700;
}

.v-dialog > .v-card > .v-card__title.headline {
  word-break: break-word;
}
</style>
